/* Site header behaviour (sections/header.liquid, snippets/mobile-menu.liquid). Loaded with defer.
   - --site-announcement-offset: the announcement bar's height, so the sticky header sits under it.
   - site-header--past-hero: on the home page, set once the hero photo has scrolled under the header
     (desktop then shows navy text on the frosted bar instead of white text over the photo).
   - The phone menu (under 990px): closes with a short animation, keeps Tab inside, Escape closes a submenu
     then the menu, a tap on the dimmed page closes it, and the footer's social links are copied in.
   - The desktop menus (990px and up): open on hover with a mouse, and the Shop and Bundles panels span the
     header's full width. */
(() => {
  const DESKTOP = '(min-width: 990px)';

  const announcementBar = document.querySelector('[data-announcement-region]') || document.querySelector('.announcement');
  const syncAnnouncementOffset = () => {
    const announcementHeight = announcementBar?.getBoundingClientRect().height || 0;
    document.documentElement.style.setProperty('--site-announcement-offset', `${announcementHeight}px`);
  };
  syncAnnouncementOffset();
  if (announcementBar && 'ResizeObserver' in window) new ResizeObserver(syncAnnouncementOffset).observe(announcementBar);
  window.addEventListener('resize', syncAnnouncementOffset, { passive: true });

  let cleanUpHeroHeader = () => {};
  const setUpHeroHeader = () => {
    cleanUpHeroHeader();
    const header = document.querySelector('.site-header--hero');
    const hero = document.querySelector('#MainContent > .shopify-section:first-child .landing-hero-image');
    if (!header || !hero) return;
    let frame = 0;
    const sync = () => {
      frame = 0;
      header.classList.toggle('site-header--past-hero', hero.getBoundingClientRect().bottom <= header.getBoundingClientRect().bottom);
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(sync);
    };
    const events = new AbortController();
    window.addEventListener('scroll', schedule, { passive: true, signal: events.signal });
    window.addEventListener('resize', schedule, { passive: true, signal: events.signal });
    window.addEventListener('pageshow', schedule, { signal: events.signal });
    const observer = new ResizeObserver(schedule);
    observer.observe(header);
    observer.observe(hero);
    sync();
    cleanUpHeroHeader = () => {
      events.abort();
      observer.disconnect();
      cancelAnimationFrame(frame);
      header.classList.remove('site-header--past-hero');
    };
  };
  setUpHeroHeader();
  document.addEventListener('shopify:section:load', setUpHeroHeader);
  document.addEventListener('shopify:section:unload', () => requestAnimationFrame(setUpHeroHeader));

  document.querySelectorAll('.mobile-menu').forEach((menu) => {
    const panel = menu.querySelector('.mobile-menu__panel');
    const desktop = window.matchMedia(DESKTOP);
    const submenus = Array.from(menu.querySelectorAll('.mobile-menu__submenu'));
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    const fillSocial = () => {
      const slot = menu.querySelector('[data-mobile-menu-social]');
      const source = document.querySelector('.site-footer .social-links');
      if (slot && source && !slot.childElementCount) slot.appendChild(source.cloneNode(true));
    };

    const closeMenu = (restoreFocus = false) => {
      if (!menu.hasAttribute('open')) return;
      const finish = () => {
        submenus.forEach((submenu) => submenu.removeAttribute('open'));
        menu.removeAttribute('open');
        menu.classList.remove('is-closing');
        if (restoreFocus) menu.querySelector('summary')?.focus();
      };
      if (reduceMotion.matches) {
        finish();
        return;
      }
      menu.classList.add('is-closing');
      window.setTimeout(finish, 170);
    };

    menu.addEventListener('toggle', fillSocial);
    if (document.readyState !== 'loading') fillSocial();
    else document.addEventListener('DOMContentLoaded', fillSocial);

    // The summary toggles the menu; while it is open, a click on it plays the closing animation first.
    menu.querySelector(':scope > summary').addEventListener('click', (event) => {
      if (!menu.open) return;
      event.preventDefault();
      closeMenu(false);
    });
    menu.querySelector('[data-mobile-menu-close]')?.addEventListener('click', () => closeMenu(true));
    panel.querySelectorAll('a').forEach((link) => link.addEventListener('click', () => closeMenu(false)));
    // The bag sits in the pill beside the menu button: its drawer opens under the menu, so the menu shuts at once.
    document.querySelectorAll('.site-header [data-cart-toggle]').forEach((bag) =>
      bag.addEventListener('click', () => {
        submenus.forEach((submenu) => submenu.removeAttribute('open'));
        menu.removeAttribute('open');
        menu.classList.remove('is-closing');
      }),
    );
    // A tap on the dimmed strip around the pills (the header row itself, not a pill) closes the menu too.
    menu.closest('.site-header__inner')?.addEventListener('click', (event) => {
      if (menu.open && event.target === event.currentTarget) closeMenu(false);
    });

    menu.addEventListener('keydown', (event) => {
      if (event.key === 'Tab' && menu.open) {
        const toggle = menu.querySelector(':scope > summary');
        const focusable = [toggle, ...panel.querySelectorAll('a[href], button, summary, input, select, textarea, [tabindex]')].filter((element) => {
          if (element.tabIndex < 0 || element.disabled || !element.getClientRects().length) return false;
          if (getComputedStyle(element).visibility === 'hidden') return false;
          let ancestor = element.parentElement;
          while (ancestor && ancestor !== panel) {
            if (ancestor.matches('details:not([open])') && !(element.tagName === 'SUMMARY' && element.parentElement === ancestor)) return false;
            ancestor = ancestor.parentElement;
          }
          return true;
        });
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (!menu.contains(document.activeElement) || (event.shiftKey && document.activeElement === first)) {
          event.preventDefault();
          (event.shiftKey ? last : first).focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
      if (event.key === 'Escape' && menu.open) {
        event.preventDefault();
        const openSubmenu = submenus.find((submenu) => submenu.hasAttribute('open'));
        if (openSubmenu) {
          openSubmenu.removeAttribute('open');
          openSubmenu.querySelector('summary')?.focus();
          return;
        }
        closeMenu(true);
      }
    });

    desktop.addEventListener('change', () => {
      if (!desktop.matches) return;
      submenus.forEach((submenu) => submenu.removeAttribute('open'));
      menu.removeAttribute('open');
      menu.classList.remove('is-closing');
    });

    /* A sign-up error from the menu form reloads the page, so reopen the menu to show it. */
    if (menu.querySelector('[data-newsletter-error]') && window.location.hash === '#MenuNewsletterForm') menu.open = true;
  });

  document.querySelectorAll('.site-header__menu').forEach((menu) => {
    const desktop = window.matchMedia(DESKTOP);
    const hoverPointer = window.matchMedia('(hover: hover) and (pointer: fine)');
    const shopPanel = menu.querySelector(':scope > .site-header__menu-panel--shop');
    let closeTimer;
    let openedByHover = false;

    const positionShopPanel = () => {
      if (!shopPanel || !desktop.matches || !menu.hasAttribute('open')) return;
      const viewportGutter = 16;
      const header = menu.closest('.site-header');
      const inner = header.querySelector('.site-header__inner');
      const headerRect = header.getBoundingClientRect();
      const innerRect = inner.getBoundingClientRect();
      const innerStyle = getComputedStyle(inner);
      const menuRect = menu.getBoundingClientRect();
      const contentLeft = innerRect.left + parseFloat(innerStyle.paddingLeft);
      const contentRight = innerRect.right - parseFloat(innerStyle.paddingRight);
      shopPanel.style.setProperty('--shop-panel-width', `${headerRect.width}px`);
      shopPanel.style.setProperty('--shop-panel-left', `${headerRect.left - menuRect.left}px`);
      shopPanel.style.setProperty('--shop-panel-padding-left', `${Math.max(0, contentLeft - headerRect.left)}px`);
      shopPanel.style.setProperty('--shop-panel-padding-right', `${Math.max(0, headerRect.right - contentRight)}px`);
      shopPanel.style.setProperty('--shop-panel-top', `${headerRect.bottom - menuRect.top}px`);
      shopPanel.style.setProperty('--shop-panel-max-height', `${Math.max(160, window.innerHeight - headerRect.bottom - viewportGutter)}px`);
      menu.style.setProperty('--shop-menu-bridge-height', `${Math.max(0, headerRect.bottom - menuRect.bottom)}px`);
    };

    const openMenu = () => {
      if (!desktop.matches) return;
      window.clearTimeout(closeTimer);
      document.querySelectorAll('.site-header__menu[open]').forEach((openSibling) => {
        if (openSibling !== menu) openSibling.removeAttribute('open');
      });
      menu.setAttribute('open', '');
      positionShopPanel();
    };

    const scheduleClose = () => {
      if (!desktop.matches) return;
      window.clearTimeout(closeTimer);
      closeTimer = window.setTimeout(() => {
        if (!menu.matches(':hover') && !menu.contains(document.activeElement)) menu.removeAttribute('open');
      }, 120);
    };

    menu.addEventListener('pointerenter', (event) => {
      if (!hoverPointer.matches || event.pointerType === 'touch') return;
      if (!menu.open) openedByHover = true;
      openMenu();
    });
    menu.addEventListener('pointerleave', scheduleClose);
    menu.addEventListener('focusin', () => window.clearTimeout(closeTimer));
    menu.addEventListener('focusout', scheduleClose);
    menu.querySelector(':scope > summary')?.addEventListener('click', (event) => {
      if (!desktop.matches) return;
      if (openedByHover && menu.open) {
        event.preventDefault();
        openedByHover = false;
      }
    });
    menu.addEventListener('toggle', () => {
      if (menu.open) openMenu();
      else openedByHover = false;
    });
    desktop.addEventListener('change', () => {
      if (!desktop.matches) menu.removeAttribute('open');
    });
    menu.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      menu.querySelector('summary')?.focus();
      menu.removeAttribute('open');
    });
    window.addEventListener('resize', positionShopPanel, { passive: true });
    window.addEventListener('scroll', positionShopPanel, { passive: true });
    document.addEventListener('pointerdown', (event) => {
      if (desktop.matches && !menu.contains(event.target)) menu.removeAttribute('open');
    });
  });
})();
